import { addChannelData, isChannelDataLoaded, shapeDataForApp } from '../utils/data';
import { isChannelFullySupported, isFeatureEnabled } from '../utils/featureFlags';
import { sortCaseIgnoreWithNone } from '../utils/array';
import { forEach } from '../utils/iterable/forEach';
import { getSize } from '../utils/iterable/getSize';
import api from '../utils/api';

import { MESSAGE, NOTIFICATION } from '../constants/notifications';
import { PROFILE, PROFILE_TYPE } from '../constants/profiles';
import { CHANNEL_DATA_KEYS } from '../constants/data';
import { ETSY, SHOPIFY } from '../constants/channels';
import { FEATURES } from '../constants/billing';
import { ENDPOINT } from '../constants/api';
import { EMPTY } from '../constants';

import actions from '../actions';

function* addProductType(reduction, { shopId, productType }) {
  let productTypes = reduction.getIn(['data', 'shopsData', shopId, 'productTypes'], EMPTY.LIST);

  if (productType && !productTypes.includes(productType)) {
    productTypes = productTypes.push(productType).sort(sortCaseIgnoreWithNone);
  }

  return reduction.setIn(['data', 'shopsData', shopId, 'productTypes'], productTypes);
}

function* addSection(reduction, { shopId, section }) {
  let sections = reduction.getIn(['data', 'shopsData', shopId, 'sections'], EMPTY.MAP);

  function updateById(byId = EMPTY.MAP) {
    return byId.set(section, section);
  }

  function updateOptions(options = EMPTY.LIST) {
    return options.push(section);
  }

  function sortSections(sectionsById) {
    return function sort(x, y) {
      return sortCaseIgnoreWithNone(sectionsById.get(x), sectionsById.get(y));
    };
  }

  if (!sections.hasIn(['byId', section])) {
    const byId = sections.get('byId').update(updateById);
    const options = sections.get('options').update(updateOptions).sort(sortSections(byId));
    sections = sections.set('byId', byId).set('options', options);
  }

  return reduction.setIn(['data', 'shopsData', shopId, 'sections'], sections);
}

function* addVariations(reduction, { option, shopId, variant }) {
  let state = reduction;
  const channel = state.getIn(['shops', 'byId', shopId, 'channel']);
  const db = state.getIn(['shops', 'byId', shopId, 'db']);

  if (variant) {
    state = state.setIn(['data', 'loading', 'variations', shopId], true);

    yield function effect(dispatch) {
      if (option) {
        api.variations
          .addOption({ channel, db, option, shopId, variant })
          .then(function onSuccess(response) {
            dispatch(actions.data.getVariations.fulfilled({ channel, data: response, shopId }));
          })
          .catch(function onFail(error) {
            console.error(error);

            dispatch(
              actions.notifications.add({
                type: NOTIFICATION.ERROR,
                message: MESSAGE.FAIL.SAVE_OPTION,
              })
            );

            dispatch(actions.data.addVariations.rejected({ shopId }));
          });
      } else {
        api.variations
          .addVariant({ channel, db, shopId, variant })
          .then(function onSuccess(response) {
            dispatch(actions.data.getVariations.fulfilled({ channel, data: response, shopId }));
          })
          .catch(function onFail(error) {
            console.error(error);

            dispatch(
              actions.notifications.add({
                type: NOTIFICATION.ERROR,
                message: MESSAGE.FAIL.SAVE_VARIANT,
              })
            );

            dispatch(actions.data.addVariations.rejected({ shopId }));
          });
      }
    };
  }

  return state;
}

function* addVariationsRejected(reduction, { shopId }) {
  return reduction.deleteIn(['data', 'loading', 'variations', shopId], true);
}

function* addVendor(reduction, { shopId, vendor }) {
  let vendors = reduction.getIn(['data', 'shopsData', shopId, 'vendors'], EMPTY.LIST);

  if (vendor && !vendors.includes(vendor)) {
    vendors = vendors.push(vendor).sort(sortCaseIgnoreWithNone);
  }

  return reduction.setIn(['data', 'shopsData', shopId, 'vendors'], vendors);
}

function* deleteVariations(reduction, { option: optionId, shopId, variant: variantId }) {
  let state = reduction;
  const channel = state.getIn(['shops', 'byId', shopId, 'channel']);
  const db = state.getIn(['shops', 'byId', shopId, 'db']);

  if (variantId) {
    const variantIndex = state
      .getIn(['data', 'shopsData', shopId, 'variations', 'variants'], EMPTY.LIST)
      .findIndex(function findVariant(item) {
        return item.get('id') === variantId;
      });

    if (variantIndex === -1) return state;

    if (optionId) {
      const variantName = state.getIn(['data', 'shopsData', shopId, 'variations', 'variants', variantIndex, 'value']);
      const optionIndex = state
        .getIn(['data', 'shopsData', shopId, 'variations', 'options', variantName], EMPTY.LIST)
        .findIndex(function findOption(item) {
          return item.get('id') === optionId;
        });

      if (optionIndex === -1) return state;

      state = state
        .setIn(['data', 'loading', 'variations', shopId], true)
        .setIn(['data', 'shopsData', shopId, 'variations', 'options', variantName, optionIndex, 'disabled'], true);

      yield function effect(dispatch) {
        api.variations
          .deleteOption({ channel, db, option: optionId, shopId, variant: variantId })
          .then(function onSuccess(response) {
            dispatch(actions.data.getVariations.fulfilled({ channel, data: response, shopId }));
          })
          .catch(function onFail(error) {
            console.error(error);

            dispatch(
              actions.notifications.add({
                type: NOTIFICATION.ERROR,
                message: MESSAGE.FAIL.DELETE_OPTION,
              })
            );

            dispatch(
              actions.data.deleteVariations.rejected({
                optionId,
                optionIndex,
                shopId,
                variantId,
                variantIndex,
                variantName,
              })
            );
          });
      };
    } else {
      state = state
        .setIn(['data', 'loading', 'variations', shopId], true)
        .setIn(['data', 'shopsData', shopId, 'variations', 'variants', variantIndex, 'disabled'], true);

      yield function effect(dispatch) {
        api.variations
          .deleteVariant({ channel, db, shopId, variant: variantId })
          .then(function onSuccess(response) {
            dispatch(actions.data.getVariations.fulfilled({ channel, data: response, shopId }));
          })
          .catch(function onFail(error) {
            console.error(error);

            dispatch(
              actions.notifications.add({
                type: NOTIFICATION.ERROR,
                message: MESSAGE.FAIL.DELETE_VARIANT,
              })
            );

            dispatch(actions.data.deleteVariations.rejected({ shopId, variantId, variantIndex }));
          });
      };
    }
  }

  return state;
}

function* deleteVariationsRejected(reduction, payload) {
  const { optionId, shopId, variantId, variantName } = payload;
  let { optionIndex, variantIndex } = payload;
  let state = reduction;

  if (variantId) {
    if (state.getIn(['data', 'shopsData', shopId, 'variations', 'variants', variantIndex, 'id']) !== variantId) {
      variantIndex = state
        .getIn(['data', 'shopsData', shopId, 'variations', 'variants'], EMPTY.LIST)
        .findIndex(function findVariant(item) {
          return item.get('id') === variantId;
        });

      if (variantIndex === -1) return state;
    }

    if (optionId) {
      if (
        state.getIn(['data', 'shopsData', shopId, 'variations', 'options', variantName, optionIndex, 'id']) !== optionId
      ) {
        optionIndex = state
          .getIn(['data', 'shopsData', shopId, 'variations', 'options', variantName], EMPTY.LIST)
          .findIndex(function findOption(item) {
            return item.get('id') === optionId;
          });

        if (optionIndex === -1) return state;

        state = state
          .deleteIn(['data', 'shopsData', shopId, 'variations', 'options', variantName, optionIndex, 'disabled']);
      }
    } else {
      state = state.deleteIn(['data', 'shopsData', shopId, 'variations', 'variants', variantIndex, 'disabled']);
    }
  }

  return state.deleteIn(['data', 'loading', 'variations', shopId]);
}

function* getAllData(reduction, shopId) {
  yield function effect(dispatch) {
    if (!reduction.hasIn(['shops', 'byId', shopId])) return;

    const channel = reduction.getIn(['shops', 'byId', shopId, 'channel']);

    if (!isChannelFullySupported(channel)) return;

    if (
      !reduction.getIn(['data', 'loading', 'channel', channel]) &&
      !reduction.getIn(['data', 'loaded', 'channel', channel])
    ) {
      dispatch(actions.data.getChannelData({ channel }));
    }

    if (
      !reduction.getIn(['data', 'loaded', 'shop', shopId]) &&
      !reduction.getIn(['data', 'loading', 'shop', shopId])
    ) {
      dispatch(actions.data.getShopData({ shopId }));
    }

    if (isFeatureEnabled({ feature: FEATURES.PROFILES_V2, userId: reduction.getIn(['user', 'userId']) })) {
      if (
        !reduction.getIn(['data', 'loaded', 'profiles', shopId, PROFILE_TYPE.STANDARD]) &&
        !reduction.getIn(['data', 'loading', 'profiles', shopId, PROFILE_TYPE.STANDARD])
      ) {
        dispatch(actions.data.getProfiles({ shopId, type: PROFILE_TYPE.STANDARD }));
      }

      if (isFeatureEnabled({ feature: FEATURES.AI_PROFILES, userId: reduction.getIn(['user', 'userId']) })) {
        if (
          !reduction.getIn(['data', 'loaded', 'profiles', shopId, PROFILE_TYPE.AI]) &&
          !reduction.getIn(['data', 'loading', 'profiles', shopId, PROFILE_TYPE.AI])
        ) {
          dispatch(actions.data.getProfiles({ shopId, type: PROFILE_TYPE.AI }));
        }
      }
    } else {
      if (
        !reduction.getIn(['data', 'loaded', 'profiles', shopId, PROFILE.LISTINGS]) &&
        !reduction.getIn(['data', 'loading', 'profiles', shopId, PROFILE.LISTINGS])
      ) {
        dispatch(actions.data.getProfiles({ shopId, type: PROFILE.LISTINGS }));
      }

      if (
        !reduction.getIn(['data', 'loaded', 'profiles', shopId, PROFILE.TAGS]) &&
        !reduction.getIn(['data', 'loading', 'profiles', shopId, PROFILE.TAGS])
      ) {
        dispatch(actions.data.getProfiles({ shopId, type: PROFILE.TAGS }));
      }

      if (
        !reduction.getIn(['data', 'loaded', 'profiles', shopId, PROFILE.VARIATIONS]) &&
        !reduction.getIn(['data', 'loading', 'profiles', shopId, PROFILE.VARIATIONS])
      ) {
        dispatch(actions.data.getProfiles({ shopId, type: PROFILE.VARIATIONS }));
      }
    }

    if (isFeatureEnabled({ feature: FEATURES.CUSTOM_VARIATIONS, userId: reduction.getIn(['user', 'userId']) })) {
      if (
        !reduction.getIn(['data', 'loaded', 'variations', shopId]) &&
        !reduction.getIn(['data', 'loading', 'variations', shopId])
      ) {
        dispatch(actions.data.getVariations({ shopId }));
      }
    }
  };

  return reduction;
}

function* getChannelData(reduction, { channel }) {
  if (
    reduction.getIn(['data', 'loaded', 'channel', channel]) ||
    reduction.getIn(['data', 'loading', 'channel', channel])
  ) {
    return reduction;
  }

  if (!CHANNEL_DATA_KEYS.hasOwnProperty(channel)) {
    return reduction;
  }

  yield function effect(dispatch) {
    function processCountries(data) {
      function addData(value, key) {
        dispatch(actions.data.getChannelData.fulfilled({ channel, data: value, key }));
      }

      forEach(shapeDataForApp.countryData(data), addData);
    }

    switch (channel) {
      case ETSY: {
        for (const key of CHANNEL_DATA_KEYS[channel]) {
          api
            .get({ endpoint: ENDPOINT.TAXONOMY, url: `/v1/${key}.json` })
            .then(
              (data) => dispatch(actions.data.getChannelData.fulfilled({ channel, data, key })),
              (error) => dispatch(actions.data.getChannelData.rejected({ channel, error })),
            );
        }

        break;
      }

      case SHOPIFY: {
        for (const key of CHANNEL_DATA_KEYS[channel]) {
          switch (key) {
            case 'TAXONOMY':
            case 'TAXONOMY_ATTRIBUTES':
            case 'TAXONOMY_ATTRIBUTE_VALUES': {
              if (
                !isFeatureEnabled({ feature: FEATURES.SHOPIFY_CATEGORY, userId: reduction.getIn(['user', 'userId']) })
              ) {
                dispatch(actions.data.getChannelData.fulfilled({ channel, data: {}, key }));
                break;
              }

              api
                .get({ endpoint: ENDPOINT.TAXONOMY, url: `/shopify/${key}.json` })
                .then(
                  (data) => dispatch(actions.data.getChannelData.fulfilled({ channel, data, key })),
                  (error) => dispatch(actions.data.getChannelData.rejected({ channel, error })),
                );

              break;
            }

            default: {
              break;
            }
          }
        }

        api
          .get({ url: '/countries' })
          .then(
            processCountries,
            (error) => dispatch(actions.data.getChannelData.rejected({ channel, error })),
          );

        break;
      }

      default: {
        break;
      }
    }
  };

  return reduction.setIn(['data', 'loading', 'channel', channel], true);
}

function* getChannelDataFulfilled(reduction, { channel, data, key }) {
  addChannelData({ channel, data, key });

  return isChannelDataLoaded(channel)
    ? reduction
      .deleteIn(['data', 'loading', 'channel', channel])
      .setIn(['data', 'loaded', 'channel', channel], true)
    : reduction;
}

function* getChannelDataRejected(reduction, { channel, error }) {
  yield function effect(dispatch) {
    console.error('Error encountered while getting channel data: ', error);
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.LOAD_DATA,
      })
    );
  };

  return reduction.deleteIn(['data', 'loading', 'channel', channel]);
}

function* getProfiles(reduction, { force = false, shopId, type }) {
  if (isFeatureEnabled({ feature: FEATURES.PROFILES_V2, userId: reduction.getIn(['user', 'userId']) })) {
    if (
      !reduction.hasIn(['shops', 'byId', shopId]) ||
      reduction.getIn(['data', 'loading', 'profiles', shopId, type]) || (
        !force && reduction.getIn(['data', 'loaded', 'profiles', shopId, type])
      )
    ) {
      return reduction;
    }

    yield function effect(dispatch) {
      const channel = reduction.getIn(['shops', 'byId', shopId, 'channel']);
      const db = reduction.getIn(['shops', 'byId', shopId, 'db']);

      api.profiles.v2
        .getProfiles({ db, shopId, type })
        .then(function onFulfilled(response) {
          dispatch(actions.data.getProfiles.fulfilled({ channel, data: response.data, shopId, type }));
        })
        .catch(function onRejected(error) {
          dispatch(actions.data.getProfiles.rejected({ error, shopId, type }));
        });
    };

    return reduction.setIn(['data', 'loading', 'profiles', shopId, type], true);
  } else if (
    reduction.getIn(['data', 'loading', 'profiles', shopId, type]) ||
    !reduction.hasIn(['shops', 'byId', shopId]) || (
      !force && reduction.getIn(['data', 'loaded', 'profiles', shopId, type])
    )
  ) {
    return reduction;
  }

  yield function effect(dispatch) {
    const db = reduction.getIn(['shops', 'byId', shopId, 'db']);

    switch (type) {
      case PROFILE.LISTINGS: {
        api.profiles
          .totals({ db, shopId, type })
          .then((totals) => {
            api.profiles
              .get({ db, limit: totals.count, offset: 0, shopId, type })
              .then((profileList) => {
                dispatch(actions.data.getProfiles.fulfilled({ data: { profiles: profileList.data }, shopId, type }));
              });
          })
          .catch((error) => dispatch(actions.data.getProfiles.rejected({ error, shopId, type })));

        break;
      }

      default: {
        api.profiles
          .getLookup({ params: { db }, shopId, type })
          .then(
            (data) => dispatch(actions.data.getProfiles.fulfilled({ data, shopId, type })),
            (error) => dispatch(actions.data.getProfiles.rejected({ error, shopId, type }))
          );
        break;
      }
    }
  };

  return reduction.setIn(['data', 'loading', 'profiles', shopId, type], true);
}

function* getProfilesFulfilled(reduction, { channel, data, shopId, type }) {
  if (isFeatureEnabled({ feature: FEATURES.PROFILES_V2, userId: reduction.getIn(['user', 'userId']) })) {
    const profiles = shapeDataForApp.profilesV2({ channel, data, type });
    return reduction
      .deleteIn(['data', 'loading', 'profiles', shopId, type])
      .setIn(['data', 'loaded', 'profiles', shopId, type], true)
      .setIn(['data', 'shopsData', shopId, 'profiles', type], profiles);
  }

  if (!data?.profiles) {
    yield function effect(dispatch) {
      dispatch(actions.data.getProfiles.rejected({ error: data.error, shopId, type }));
    };

    return reduction;
  }

  const profiles = shapeDataForApp.profiles({ profiles: data.profiles, type, userShops: reduction.get('shops') });

  return reduction
    .deleteIn(['data', 'loading', 'profiles', shopId, type])
    .setIn(['data', 'loaded', 'profiles', shopId, type], true)
    .setIn(['data', 'shopsData', shopId, 'profiles', type], profiles);
}

function* getProfilesRejected(reduction, { error, shopId, type }) {
  yield function effect(dispatch) {
    console.error('Error encountered while getting profiles lookup: ', error);
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.LOAD_PROFILES,
      })
    );
  };

  return reduction.deleteIn(['data', 'loading', 'profiles', shopId, type]);
}

function* getShopData(reduction, { force = false, shopId }) {
  if (
    reduction.getIn(['data', 'loading', 'shop', shopId]) ||
    !reduction.hasIn(['shops', 'byId', shopId]) || (
      !force && reduction.getIn(['data', 'loaded', 'shop', shopId])
    )
  ) {
    return reduction;
  }

  const channel = reduction.getIn(['shops', 'byId', shopId, 'channel']);
  const params = {
    db: reduction.getIn(['shops', 'byId', shopId, 'db']),
  };

  if (!isChannelFullySupported(channel)) {
    return reduction
      .setIn(['data', 'loaded', 'shop', shopId], true)
      .setIn(['data', 'shopsData', shopId], EMPTY.MAP);
  }

  yield function effect(dispatch) {
    const requests = [
      api.get({ url: `/shops/${shopId}/channelData`, params }),
    ];

    switch (channel) {
      case ETSY: {
        requests.push(
          api.get({ url: `/shops/${shopId}/productionPartners`, params }),
          api.get({ url: `/shops/${shopId}/returnPolicies`, params }),
          api.get({ url: `/shops/${shopId}/shippingTemplates`, params }),
        );

        break;
      }

      default: {
        break;
      }
    }

    Promise.all(requests)
      .then(
        (responses) => dispatch(actions.data.getShopData.fulfilled({ channel, responses, shopId })),
        (error) => dispatch(actions.data.getShopData.rejected({ error, shopId }))
      );
  };

  return reduction.setIn(['data', 'loading', 'shop', shopId], true);
}

function* getShopDataFulfilled(reduction, { channel, responses, shopId }) {
  function updateShopData(source = EMPTY.MAP) {
    const [shopData] = responses;
    let result = source;

    for (const [key, value] of Object.entries(shapeDataForApp.shopData({ channel, shopData }))) {
      result = result.set(key, value);
    }

    switch (channel) {
      case ETSY: {
        const [
          ,
          { productionPartners } = {},
          { returnPolicies } = {},
          { shippingTemplates } = {},
        ] = responses;

        result = result
          .set('productionPartners', shapeDataForApp.productionPartners(productionPartners))
          .set('returnPolicies', shapeDataForApp.returnPolicies(returnPolicies))
          .set('shippingProfiles', shapeDataForApp.shippingProfiles(shippingTemplates));

        break;
      }

      default: {
        break;
      }
    }

    return result;
  }

  return reduction
    .deleteIn(['data', 'loading', 'shop', shopId])
    .setIn(['data', 'loaded', 'shop', shopId], true)
    .updateIn(['data', 'shopsData', shopId], updateShopData);
}

function* getShopDataRejected(reduction, { error, shopId }) {
  yield function effect(dispatch) {
    console.error('Error encountered while getting shop data: ', error);
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.LOAD_DATA,
      })
    );
  };

  return reduction.deleteIn(['data', 'loading', 'shop', shopId], true);
}

function* getVariations(reduction, { force = false, shopId }) {
  if (
    !reduction.hasIn(['shops', 'byId', shopId]) ||
    reduction.getIn(['data', 'loading', 'variations', shopId]) || (
      !force && reduction.getIn(['data', 'loaded', 'variations', shopId])
    )
  ) {
    return reduction;
  }

  yield function effect(dispatch) {
    const channel = reduction.getIn(['shops', 'byId', shopId, 'channel']);
    const db = reduction.getIn(['shops', 'byId', shopId, 'db']);

    api.variations.getVariations({ channel, db, shopId })
      .then(function onFulfilled(response) {
        dispatch(actions.data.getVariations.fulfilled({ channel, data: response, shopId }));
      })
      .catch(function onRejected(error) {
        dispatch(actions.data.getVariations.rejected({ error, shopId }));
      });
  };

  return reduction.setIn(['data', 'loading', 'variations', shopId], true);
}

function* getVariationsFulfilled(reduction, { channel, data, shopId }) {
  const variations = shapeDataForApp.variations({ channel, data });
  return reduction
    .deleteIn(['data', 'loading', 'variations', shopId])
    .setIn(['data', 'loaded', 'variations', shopId], true)
    .setIn(['data', 'shopsData', shopId, 'variations'], variations);
}

function* getVariationsRejected(reduction, { error, shopId }) {
  yield function effect(dispatch) {
    console.error('Error encountered while getting variations: ', error);
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.LOAD_DATA,
      })
    );
  };

  return reduction.deleteIn(['data', 'loading', 'variations', shopId]);
}

function* setData(reduction, { path, value }) {
  const fullPath = Array.isArray(path)
    ? ['data', ...path]
    : ['data', path];

  if (value !== undefined) return reduction.setIn(fullPath, value);

  let state = reduction.deleteIn(fullPath);

  fullPath.pop();

  while (fullPath.length > 1) {
    if (!getSize(state.getIn(fullPath))) {
      state = state.deleteIn(fullPath);
    }

    fullPath.pop();
  }

  return state;
}

const data = {
  [actions.data.addProductType]: addProductType,
  [actions.data.addSection]: addSection,
  [actions.data.addVariations]: addVariations,
  [actions.data.addVariations.rejected]: addVariationsRejected,
  [actions.data.addVendor]: addVendor,
  [actions.data.deleteVariations]: deleteVariations,
  [actions.data.deleteVariations.rejected]: deleteVariationsRejected,
  [actions.data.getAllData]: getAllData,
  [actions.data.getChannelData]: getChannelData,
  [actions.data.getChannelData.fulfilled]: getChannelDataFulfilled,
  [actions.data.getChannelData.rejected]: getChannelDataRejected,
  [actions.data.getProfiles]: getProfiles,
  [actions.data.getProfiles.fulfilled]: getProfilesFulfilled,
  [actions.data.getProfiles.rejected]: getProfilesRejected,
  [actions.data.getShopData]: getShopData,
  [actions.data.getShopData.fulfilled]: getShopDataFulfilled,
  [actions.data.getShopData.rejected]: getShopDataRejected,
  [actions.data.getVariations]: getVariations,
  [actions.data.getVariations.fulfilled]: getVariationsFulfilled,
  [actions.data.getVariations.rejected]: getVariationsRejected,
  [actions.data.setData]: setData,
};

export default data;
